import { useEffect, useCallback, useState, useRef } from "react";

import TicketService from "src/Services/API/ticket.service";
import {
  connectTicketMessagesSocket,
  disconnectTicketMessagesSocket,
} from "src/Services/socket/ticketMessagesSocket";

/**
 * Custom hook that manages the "unread ticket" red dot indicator on the sidemenu.
 *
 * This hook is mounted inside the SideMenu component, which is always visible
 * throughout the app's lifetime. It drives the red dot next to "Ticket Management"
 * (or "Report Bug" for non-admin users).
 *
 * Unread detection works through two complementary mechanisms:
 *
 * 1. REST API check (GET /api/ticket/has_unread)
 *    - Called once on mount to catch messages that arrived while the app was closed.
 *    - Called again whenever the browser tab regains focus (visibilitychange event).
 *    - Called after a "read" socket event (debounced 500ms) to re-evaluate whether
 *      any unread tickets remain after the user opens one.
 *
 * 2. WebSocket real-time events ("ticket-new-message-notification")
 *    - On connect, subscribes to the global "ticket-notifications" room on the server.
 *    - When a new message is created (action: "created"), immediately sets hasUnread=true
 *      to show the red dot without waiting for an API round-trip.
 *    - When a ticket is marked as read (action: "read"), debounces an API re-check
 *      to determine if the dot should be removed (the last unread ticket was opened).
 *
 * This hook is the ONLY owner of the "ticket-notifications" room subscription.
 * Other hooks (e.g. useTicketListRealtime) attach their own listeners to the same
 * shared socket but do NOT subscribe/unsubscribe from the room, preventing the
 * subscription from being accidentally dropped during page navigation.
 *
 * The socket instance is a singleton with reference counting (see ticketMessagesSocket.js),
 * so it stays alive even when other hooks (e.g. useTicketMessagesRealtime in TicketDetail)
 * connect/disconnect for per-ticket rooms.
 *
 * @returns {boolean} hasUnread - true if there are unread tickets, false otherwise.
 */
const useTicketUnreadIndicator = () => {
  const [hasUnread, setHasUnread] = useState(false);
  const checkTimerRef = useRef(null);

  /** Calls the has_unread API and updates state with the result. */
  const checkApi = useCallback(async () => {
    try {
      const res = await TicketService.hasUnread();
      setHasUnread(res.data?.hasUnread === true);
    } catch {
      // ignore — API may be unavailable during startup
    }
  }, []);

  /** Initial check on mount: detects messages that arrived while the app was closed. */
  useEffect(() => {
    checkApi();
  }, [checkApi]);

  /**
   * Socket event handler for "ticket-new-message-notification".
   * - action "created": A new message was sent → immediately show the red dot.
   * - action "read": A ticket was marked as read → debounce an API re-check
   *   to see if any unread tickets remain (removes the dot if none left).
   */
  const handleNotification = useCallback(
    (payload) => {
      if (!payload?.ticketId) return;
      if (payload.action === "created") {
        setHasUnread(true);
      }
      if (payload.action === "read") {
        clearTimeout(checkTimerRef.current);
        checkTimerRef.current = setTimeout(checkApi, 500);
      }
    },
    [checkApi],
  );

  /**
   * Sets up the WebSocket connection and event listeners.
   * - Subscribes to the "ticket-notifications" room on connect.
   * - Re-subscribes and re-checks the API when the browser tab regains focus
   *   (handles cases where the socket was disconnected while the tab was hidden).
   * - Cleans up listeners and unsubscribes on unmount (practically never,
   *   since the sidemenu is always mounted).
   */
  useEffect(() => {
    const socket = connectTicketMessagesSocket();

    const subscribe = () => {
      socket.emit("ticket-notifications:subscribe");
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const s = connectTicketMessagesSocket();
      s.emit("ticket-notifications:subscribe");
      checkApi();
    };

    socket.on("connect", subscribe);
    socket.on("ticket-new-message-notification", handleNotification);

    if (socket.connected) {
      subscribe();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(checkTimerRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      socket.off("connect", subscribe);
      socket.off("ticket-new-message-notification", handleNotification);
      socket.emit("ticket-notifications:unsubscribe");
      disconnectTicketMessagesSocket();
    };
  }, [handleNotification, checkApi]);

  return hasUnread;
};

export default useTicketUnreadIndicator;
